import { adminApi } from "../../lib/axios";

// Types for Admin FAQ CRUD
export interface FAQ {
  id: string;
  question: string;
  answer: string;
  priority?: number;
  targetAudience?: ("BRAND" | "AFFILIATE" | "AGENCY")[];
  createdAt?: string;
  updatedAt?: string;
}

// Admin FAQ Service with full CRUD operations
export const adminFaqService = {
  // GET - Fetch all FAQs with optional filters
  getFAQs: async (): Promise<FAQ[]> => {
    try {
      const response = await adminApi.get("/faq");
      return response.data.data || response.data.faqs || response.data;
    } catch (error: any) {
      console.error("Error fetching FAQs:", error);
      return [];
    }
  },

  // GET - Fetch single FAQ by ID
  getFAQById: async (id: string): Promise<FAQ | null> => {
    try {
      const response = await adminApi.get(`/faq/${id}`);
      return response.data.data || response.data.faq || response.data;
    } catch (error: any) {
      console.error("Error fetching FAQ by ID:", error);
      return null;
    }
  },

  // POST - Create new FAQ
  createFAQ: async (
    faqData: Partial<FAQ>
  ): Promise<FAQ> => {
    try {
      const response = await adminApi.post("/faq", faqData);
      return response.data.data || response.data.faq || response.data;
    } catch (error: any) {
      console.error("Error creating FAQ:", error);
      throw new Error(error.response?.data?.message || "Failed to create FAQ");
    }
  },

  // PUT - Update existing FAQ
  updateFAQ: async (
    id: string,
    faqData: Partial<Omit<FAQ, "id" | "createdAt" | "updatedAt">>
  ): Promise<FAQ> => {
    try {
      const response = await adminApi.patch(`/faq/${id}`, faqData);
      return response.data.data || response.data.faq || response.data;
    } catch (error: any) {
      console.error("Error updating FAQ:", error);
      throw new Error(error.response?.data?.message || "Failed to update FAQ");
    }
  },

  // DELETE - Delete FAQ by ID
  deleteFAQ: async (
    id: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await adminApi.delete(`/faq/${id}`);
      return {
        success: true,
        message: response.data.message || "FAQ deleted successfully",
      };
    } catch (error: any) {
      console.error("Error deleting FAQ:", error);
      throw new Error(error.response?.data?.message || "Failed to delete FAQ");
    }
  },

  // PATCH - Update FAQ status/priority (partial update)
  updateFAQStatus: async (
    id: string,
    updates: Partial<Omit<FAQ, "id" | "createdAt" | "updatedAt">>
  ): Promise<FAQ> => {
    try {
      const response = await adminApi.patch(`/faq/${id}`, updates);
      return response.data.data || response.data.faq || response.data;
    } catch (error: any) {
      console.error("Error updating FAQ status:", error);
      throw new Error(
        error.response?.data?.message || "Failed to update FAQ status"
      );
    }
  },

  // Bulk operations
  bulkDeleteFAQs: async (
    ids: string[]
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await adminApi.delete("/faq/bulk", {
        data: { ids },
      });
      return {
        success: true,
        message:
          response.data.message || `${ids.length} FAQs deleted successfully`,
      };
    } catch (error: any) {
      console.error("Error bulk deleting FAQs:", error);
      throw new Error(error.response?.data?.message || "Failed to delete FAQs");
    }
  },

  // Reorder FAQs (update priorities)
  reorderFAQs: async (
    faqOrders: Array<{ id: string; priority: number }>
  ): Promise<FAQ[]> => {
    try {
      const response = await adminApi.patch("/faq/reorder", {
        faqOrders,
      });
      return response.data.data || response.data.faqs || response.data;
    } catch (error: any) {
      console.error("Error reordering FAQs:", error);
      throw new Error(
        error.response?.data?.message || "Failed to reorder FAQs"
      );
    }
  },

  // Get FAQ statistics
  getFAQStats: async (): Promise<{
    total: number;
    byAudience: Record<string, number>;
    success: boolean;
    message?: string;
  }> => {
    try {
      const response = await adminApi.get("/faq/stats");
      return {
        total: response.data.total || 0,
        byAudience: response.data.byAudience || {},
        success: true,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error("Error fetching FAQ stats:", error);
      return {
        total: 0,
        byAudience: {},
        success: false,
        message:
          error.response?.data?.message || "Failed to fetch FAQ statistics",
      };
    }
  },
};

export default adminFaqService;
